import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/Button';
import { Label } from '../ui/Label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../ui/Card.jsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/Select';
import { X } from 'lucide-react';
import { toast } from 'react-toastify';
import { updateUserRole } from '../../services/api';

const roleOptions = [
    { value: 'ROLE_PARTICIPANTE', label: 'Participante' },
    { value: 'ROLE_ADMIN', label: 'Administrador' },
];

function ChangeUserRoleModal({ isOpen, onClose, userId, currentRole, onSuccess }) {
    const initialFocusRef = useRef(null);
    const [selectedRole, setSelectedRole] = useState(currentRole);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (isOpen) {
            setSelectedRole(currentRole);
            setError('');
            
            // Foca no Select Trigger (que é o primeiro elemento interativo)
            setTimeout(() => {
                if (initialFocusRef.current) {
                    initialFocusRef.current.focus();
                }
            }, 100);
        }
    }, [isOpen, currentRole]);

    if (!isOpen) {
        return null;
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsSubmitting(true);

        if (!selectedRole) {
            setError('Por favor, selecione um papel.');
            setIsSubmitting(false);
            return;
        }

        try {
            await updateUserRole(userId, selectedRole);
            toast.success(`Papel do usuário atualizado para "${roleOptions.find(opt => opt.value === selectedRole)?.label}" com sucesso!`);
            onSuccess();
        } catch (err) {
            console.error('Erro ao atualizar papel:', err);
            setError(err.message || 'Ocorreu um erro ao tentar atualizar o papel do usuário.');
            toast.error(err.message || 'Erro ao atualizar papel.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div 
            className="fixed inset-0 bg-black/60 z-50 flex justify-center items-center p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="change-role-title"
        >
            <Card className="w-full max-w-sm relative">
                <button 
                    onClick={onClose} 
                    className="absolute top-4 right-4 p-1 rounded-full hover:bg-accent transition-colors"
                    aria-label="Fechar"
                >
                    <X className="h-5 w-5" />
                </button>
                <CardHeader>
                    <CardTitle id="change-role-title" className="mb-2">Alterar Papel do Usuário</CardTitle>
                    <CardDescription className="leading-relaxed">
                        Selecione o novo papel para este usuário.
                    </CardDescription>
                </CardHeader>
                <form onSubmit={handleSubmit}>
                    <CardContent className="space-y-4">
                        {error && <p className="text-sm font-medium text-destructive">{error}</p>}

                        <div className="grid gap-2">
                            <Label htmlFor="role" className="mb-1">Papel</Label>
                            <Select onValueChange={setSelectedRole} value={selectedRole} disabled={isSubmitting}>
                                <SelectTrigger id="role" ref={initialFocusRef}>
                                    <SelectValue placeholder="Selecione o Papel" />
                                </SelectTrigger>
                                <SelectContent>
                                    {roleOptions.map(option => (
                                        <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </CardContent>
                    <CardFooter className="flex flex-col sm:flex-row justify-end gap-2">
                        <Button type="button" variant="secondary" onClick={onClose} disabled={isSubmitting} className="w-full sm:w-auto">
                            Cancelar
                        </Button>
                        <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto">
                            {isSubmitting ? 'A guardar...' : 'Guardar Papel'}
                        </Button>
                    </CardFooter>
                </form>
            </Card>
        </div>
    );
}

export default ChangeUserRoleModal;